import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SanctumAuthService } from '@freesixtyfive/auth';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { ToastModule } from 'primeng/toast';
import { of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { UserService } from '../../shared/services/user.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    ButtonModule,
    PasswordModule,
    CardModule,
    ToastModule,
    FloatLabelModule,
    TranslateModule,
  ],
  providers: [MessageService],
  templateUrl: './login-page.component.html',
  styleUrl: './login-page.component.scss',
})
export class LoginPageComponent implements OnInit {
  email = '';
  password = '';
  loading = false;

  #router = inject(Router);
  #route = inject(ActivatedRoute);
  #messageService = inject(MessageService);
  #auth = inject(SanctumAuthService);
  #translate = inject(TranslateService);
  #user = inject(UserService);

  ngOnInit(): void {
    // If a valid session already exists when the login page initializes (e.g.
    // the authReturnGuard's 750ms cold-load timer lost the race and redirected
    // here despite a resolving token), skip the login form and resume the
    // attempted URL so the NFC check-in continues without re-entering creds.
    if (this.#auth.isAuth()) {
      this.#redirectAfterAuth();
    }
  }

  /**
   * Navigate to the validated `returnUrl` query param, falling back to `/start`.
   * Only app-internal paths are honoured: the value must start with a single
   * `/` and must not start with `//` or `/\` (protocol-relative URLs that would
   * allow an open redirect to an external host).
   */
  #redirectAfterAuth(): void {
    const returnUrl = this.#route.snapshot.queryParamMap.get('returnUrl');
    if (
      returnUrl &&
      returnUrl.startsWith('/') &&
      !returnUrl.startsWith('//') &&
      !returnUrl.startsWith('/\\')
    ) {
      this.#router.navigateByUrl(returnUrl);
    } else {
      this.#router.navigate(['/start']);
    }
  }

  onLogin() {
    if (!this.email || !this.password) {
      this.#messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please enter both email and password',
      });
      return;
    }

    this.loading = true;

    this.#auth
      .signIn({ email: this.email, password: this.password }, true)
      // this.#authService
      //   .login({ email: this.email, password: this.password })
      .pipe(
        catchError(() => {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translate.instant('error.loginFailed'),
            detail: this.#translate.instant('error.loginFailedDetail'),
          });
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: (response) => {
          if (response) {
            this.#user.setUser(response.data);
            this.#messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Login successful',
            });
            // Return the user to where they were headed before being sent to
            // login (e.g. an NFC check-in URL), defaulting to the start page.
            // Only honour app-internal paths to avoid open-redirects.
            this.#redirectAfterAuth();
          }
        },
      });
  }

  showForgotPassword(e: any) {
    e.preventDefault();
    this.#router.navigate(['/forgot-password']);
  }
}
